import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Avatar } from '../components/ui/Avatar';

import { usePatients } from './api';
import { formatPhone } from './format';

/** Espera después de la última tecla (RF-GEN-04). */
export const SEARCH_DEBOUNCE_MS = 250;

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

/**
 * Búsqueda global (RF-GEN-04): nombre sin acentos y parcial, CI, teléfono o n° de ficha; hasta 10
 * resultados mientras se escribe. Enter abre el resaltado (el primero); sin resultados, la lista.
 * Combobox ARIA: flechas para moverse, Escape cierra. `/` enfoca la búsqueda (RF-GEN-07).
 */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const listId = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const q = useDebounced(text.trim(), SEARCH_DEBOUNCE_MS);
  const results = usePatients({ q, limit: 10 }, q !== '');
  const rows = q === '' ? [] : (results.data?.data ?? []);
  const show = open && q !== '' && !results.isPending;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target !== null &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      if (event.key === '/' && !typing) {
        event.preventDefault();
        document.getElementById('global-search')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const go = (path: string) => {
    setOpen(false);
    setText('');
    void navigate(path);
  };

  return (
    <form
      role="search"
      className="search-box"
      onSubmit={(event) => {
        event.preventDefault();
        const target = rows[Math.min(active, rows.length - 1)];
        if (target !== undefined && q === text.trim()) go(`/patients/${target.id}`);
        else if (text.trim() !== '') go(`/patients?q=${encodeURIComponent(text.trim())}`);
      }}
    >
      <label className="sr-only" htmlFor="global-search">
        {t('topbar.searchLabel')}
      </label>
      <input
        id="global-search"
        type="search"
        className="in search"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={show}
        aria-controls={listId}
        {...(show &&
          rows[active] !== undefined && { 'aria-activedescendant': `${listId}-${String(active)}` })}
        autoComplete="off"
        placeholder={t('topbar.searchPlaceholder')}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          // Deja terminar el clic en un resultado.
          setTimeout(() => {
            setOpen(false);
          }, 150);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((i) => Math.min(i + 1, Math.max(rows.length - 1, 0)));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {/* Sin resultados no hay listbox vacío (axe: aria-required-children): un aviso con el mismo id. */}
      {show && rows.length === 0 && (
        <div id={listId} role="status" className="search-results muted small" style={{ padding: 10 }}>
          {t('patients.noMatches')}
        </div>
      )}
      {show && rows.length > 0 && (
        <ul id={listId} role="listbox" className="search-results" aria-label={t('topbar.searchResults')}>
          {rows.map((p, i) => (
            <li
              key={p.id}
              id={`${listId}-${String(i)}`}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => {
                setActive(i);
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                go(`/patients/${p.id}`);
              }}
            >
              <Avatar id={p.id} name={p.fullName} photoUrl={p.photoUrl} />
              <span className="col" style={{ gap: 2 }}>
                <strong>{p.fullName}</strong>
                <span className="small muted">
                  {[p.nationalId, formatPhone(p.phone), `${t('patients.chartNumber')} ${p.chartNumber}`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}
